import { derecurse } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects';
import { AStarPathfinder, PathfinderScenario, PathRoute } from '../../../platform/AStar';
import { Point } from '../../../platform/geom/Point';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { ActiveObject, resolve } from '../../Container';
import { JiraCard } from '../atlassian/JiraCard';
import { IndexCard } from '../paper/IndexCard';
import { Sticky } from '../paper/Sticky';
import { TaskCard } from '../paper/TaskCard';
import { Topic } from './Topic';
import { TopicAssocation, TopicBoundaryAttr, TopicSuspendedAttr } from './TopicAssociations';
import { TopicIndex } from './TopicIndex';

export class TopicMechanics extends ActiveObject {

    @resolve(ObjectManager) objects!: ObjectManager;
    @resolve(EditorSession) session!: EditorSession;
    @resolve(SpatialIndex) spatialIndex!: SpatialIndex;
    @resolve(TopicIndex) topicIndex!: TopicIndex;

    public start(): void {
        const { objects, topicIndex } = this;
        this.burden(
            objects.createTrigger('Topic.AutoSize')
                .for(Topic).onCreated().onChildAdded().onChildRemoved().onChangesTo('label|value' as any, 'label')
                .doForEach(this.doTopicAutoSize),
            objects.createTrigger('Topic.AssignStickies')
                .for(Sticky).onChangesTo('x', 'y')
                .do(this.doTopicAssignmentTick),
            objects.createTrigger('Topic.AssignIndexCards')
                .for(IndexCard).onChangesTo('x', 'y')
                .do(this.doTopicAssignmentTick),
            objects.createTrigger('Topic.AssignTaskCards')
                .for(TaskCard).onChangesTo('x', 'y')
                .do(this.doTopicAssignmentTick),
            objects.createTrigger('Topic.AssignJiraCards')
                .for(JiraCard).onChangesTo('x', 'y')
                .do(this.doTopicAssignmentTick),
            objects.on('Topic', 'affected', ([t]) => this.doUpdateTopicBoundary(t as Topic)),
            topicIndex.on('updated', this.doTopicUpdate),
        );
        for (const t of this.objects.getAll(Topic)) {
            this.doUpdateTopicBoundary(t);
        }
    }

    private doTopicAutoSize = (t: Topic) => {
        if (!t.alive) return;

        const before = t.size();
        const after = t.getPreferredSize();
        if (before.equals(after)) return;

        if (t.width !== after.w) t.width = after.w;
        if (t.height !== after.h) t.height = after.h;

        t.moveBy(-(after.w - before.w) / 2, -(after.h - before.h) / 2);
    };

    private doTopicUpdate = (updated: string[]) => {
        for (const id of updated) {
            const topic = this.objects.getMaybe(id, Topic);
            if (topic) {
                this.doUpdateTopicBoundary(topic);
            }
        }
    };

    private doUpdateTopicBoundary = derecurse((topic: Topic) => {
        if (TopicSuspendedAttr.get(topic)) return;

        const assignees = this.topicIndex.lookup(topic.id);
        const boundary = Topic.computeBoundary(topic, assignees);

        TopicBoundaryAttr.set(topic, boundary);
    });

    private doTopicAssignmentTick = (elmts: Physical[]) => {

        const allTopics = Array.from(this.objects.getAll(Topic));

        for (const elmt of elmts) {

            const current = TopicAssocation.get(elmt);
            if (current) {
                // Should we remove ?
                const topic = this.objects.getMaybe(current, Topic);
                if (topic && this.hasElementTopicPath(elmt, topic)) continue;

                TopicAssocation.set(elmt, null); // TODO: Revaluate

            }
            else {
                // Should we add ?
                const topic = allTopics.find(t =>
                    this.isElementInsideTopicAreaOfEffect(elmt, t) ||
                    this.isElementInsideTopicBoundary(elmt, t)
                );
                if (!topic) continue;

                TopicAssocation.set(elmt, topic.id);
            }
        }

    };

    private isElementInsideTopicAreaOfEffect(elmt: Physical, topic: Topic) {
        const ec = elmt.center(RelativeTo.Model);
        const th = topic.bounds(RelativeTo.Model).inflate([200, 200]);
        return th.contains(ec);
    }

    private isElementInsideTopicBoundary(elmt: Physical, topic: Topic) {
        if (this.isElementInsideTopicAreaOfEffect(elmt, topic)) return true;
        const tb = TopicBoundaryAttr.get(topic);
        const trt = topic.transform(RelativeTo.Model).inverse();
        const points = trt.apply(elmt.bounds(RelativeTo.Model).points());
        for (const p of points) {
            if (tb?.contains(p)) return true;
        }
        return false;
    }

    private hasElementTopicPath(elmt: Physical, topic: Topic) {
        if (this.isElementInsideTopicAreaOfEffect(elmt, topic)) return true;
        const nodes = new Set(Array.from(this.topicIndex.lookup(topic.id)).concat([topic]));
        const scn = new ValidateElementTopicPathScenario(nodes, this.spatialIndex);
        const finder = new AStarPathfinder(scn);
        const result = finder.search(elmt, topic);
        return !!result;
    }
}

class ValidateElementTopicPathScenario implements PathfinderScenario<Physical> {

    private goal = null as Physical;
    private goalFrame = null as Point[];

    constructor(private nodes: Set<Physical>, private spatialIndex: SpatialIndex) {
    }

    public init(goal: Physical): void {
        this.goal = goal;
        this.goalFrame = goal.bounds(RelativeTo.Model).toDotFrame(100);
    }

    public cleanUp(): void {
        this.goal = null;
        this.goalFrame = null;
    }

    public estimate(from: Physical, _goal: Physical): number {
        // Use direct distance heuristic
        const fp = from.center(RelativeTo.Model);
        return Math.min(...this.goalFrame.map(x => x.distanceTo(fp)));
    }

    public routesFrom(location: Physical): PathRoute<Physical>[] {
        const routes = [] as PathRoute<Physical>[];

        const lp = location.center(RelativeTo.Model);
        const lbi = location.bounds(RelativeTo.Model).inflate([150, 150]);
        const neighbours = this.spatialIndex.lookup(lbi);

        for (const dest of neighbours) {
            if (!this.nodes.has(dest)) continue;
            const cost = dest === this.goal
                ? this.estimate(location, dest)
                : lp.distanceTo(dest.center(RelativeTo.Model));

            if (cost > 400) continue;
            routes.push({ dest, cost });
        }

        return routes;
    }
}
